import BaseApiService from './BaseApiService'
import type { User } from '../types'

export interface LoginResponse {
  requiresMfa?: boolean
  mfaPendingToken?: string
  requiresEmailVerification?: boolean
  message?: string
}

export interface RegisterResponse {
  success?: boolean
  message?: string
  requiresEmailVerification?: boolean
}

export interface OAuthSignInResponse {
  success: boolean
  requiresMfa?: boolean
}

export interface MfaStatusResponse {
  isEnabled: boolean
  /** 10 = email code, 20 = authenticator app (TOTP). */
  methodId?: number
  /** When MFA becomes mandatory for this (admin) account; absent when it never is. */
  requiredBy?: string | null
}

export interface MfaEnrollResponse {
  qrCodeUrl?: string
  method: string
}

export interface MfaEnrollConfirmResponse {
  recoveryCodes?: string[]
}

export interface MfaRecoveryCodesResponse {
  recoveryCodes: string[]
}

export interface MfaRecoveryCodesCountResponse {
  count: number
}

class AuthApiService extends BaseApiService {
  register(payload: {
    emailAddress: string
    password: string
    firstName: string
    lastName: string
    teamName: string
    turnstileToken?: string
  }) {
    return this.post<RegisterResponse>('/v1/auth/register', payload)
  }

  login(payload: { emailAddress: string; password: string; turnstileToken?: string }) {
    return this.post<LoginResponse>('/v1/auth/login', payload)
  }

  /** Turnstile widget config for the public auth forms: whether the flag is on and the public site key. */
  turnstileConfig() {
    return this.post<{ enabled: boolean; siteKey: string }>('/v1/feature-flags/turnstile')
  }

  verifyEmail(token: string) {
    return this.post<void>('/v1/auth/verify-email', { token })
  }

  verifyEmailCode(emailAddress: string, code: string) {
    return this.post<void>('/v1/auth/verify-email/code', { emailAddress, code })
  }

  resendVerification(emailAddress: string) {
    return this.post<void>('/v1/auth/resend-verification', { emailAddress })
  }

  logout() {
    return this.post<void>('/v1/auth/logout')
  }

  me() {
    return this.postSilent<User>('/v1/auth/me')
  }

  updateProfile(firstName: string, lastName: string | null) {
    return this.post<{ success: boolean }>('/v1/auth/update-profile', { firstName, lastName })
  }

  changePassword(currentPassword: string, newPassword: string) {
    return this.post<{ success: boolean }>('/v1/auth/change-password', { currentPassword, newPassword })
  }

  changeEmail(newEmail: string, password: string) {
    return this.post<{ success: boolean }>('/v1/auth/change-email', { newEmail, password })
  }

  verifyEmailChange(code: string) {
    return this.post<{ success: boolean }>('/v1/auth/verify-email-change', { code })
  }

  signOutEverywhere() {
    return this.post<{ success: boolean }>('/v1/auth/sign-out-everywhere')
  }

  mfaVerify(code: string) {
    return this.post<void>('/v1/auth/mfa/verify', { code })
  }

  mfaStatus() {
    return this.post<MfaStatusResponse>('/v1/auth/mfa/status')
  }

  mfaEnroll() {
    return this.post<MfaEnrollResponse>('/v1/auth/mfa/enroll')
  }

  mfaEnrollConfirm(code: string) {
    return this.post<MfaEnrollConfirmResponse>('/v1/auth/mfa/enroll/confirm', { code })
  }

  /** Starts authenticator-app enrollment: secret for manual entry + otpauth URI (QR / tap-to-add). */
  mfaTotpEnroll() {
    return this.post<{ secret: string; otpauthUri: string }>('/v1/auth/mfa/totp/enroll')
  }

  mfaTotpConfirm(code: string) {
    return this.post<MfaEnrollConfirmResponse>('/v1/auth/mfa/totp/enroll/confirm', { code })
  }

  mfaDisable(code: string) {
    return this.post<void>('/v1/auth/mfa/disable', { code })
  }

  mfaGenerateRecoveryCodes() {
    return this.post<MfaRecoveryCodesResponse>('/v1/auth/mfa/recovery-codes/generate')
  }

  mfaRecoveryCodesCount() {
    return this.post<MfaRecoveryCodesCountResponse>('/v1/auth/mfa/recovery-codes/count')
  }

  mfaRecover(code: string) {
    return this.post<void>('/v1/auth/mfa/recover', { code })
  }

  forgotPassword(emailAddress: string, turnstileToken?: string) {
    return this.post<void>('/v1/auth/forgot-password', { emailAddress, turnstileToken })
  }

  resetPassword(token: string, newPassword: string) {
    return this.post<void>('/v1/auth/reset-password', { token, newPassword })
  }

  /** Single-use nonce for the next federated sign-in (also set as an HttpOnly cookie). */
  oauthNonce() {
    return this.post<{ nonce: string }>('/v1/auth/oauth/nonce')
  }

  // requiresMfa: the account has MFA, so the session is pending until /mfa verifies the code.
  oauthGoogle(idToken: string) {
    return this.post<OAuthSignInResponse>('/v1/auth/oauth/google', { idToken })
  }

  oauthMicrosoft(idToken: string) {
    return this.post<OAuthSignInResponse>('/v1/auth/oauth/microsoft', { idToken })
  }

  oauthApple(idToken: string, firstName?: string, lastName?: string) {
    return this.post<OAuthSignInResponse>('/v1/auth/oauth/apple', { idToken, firstName, lastName })
  }

  validateInvite(token: string) {
    return this.post<{ email: string }>('/v1/auth/invite/validate', { token })
  }

  acceptInvite(token: string, password: string, firstName: string, lastName: string) {
    return this.post<{ userId: string; email: string }>('/v1/auth/invite/accept', { token, password, firstName, lastName })
  }
}

export const authApiService = new AuthApiService()
